import { useState, useEffect, useRef } from "react";
import { getDayPhase } from "@/components/OceanWeather";
import { getSceneImage } from "@/components/IslandScene";

const SKY = {
  dawn: "linear-gradient(180deg, #2b2a55 0%, #b06a7c 45%, #f2a65e 100%)",
  day: "linear-gradient(180deg, #79c4f2 0%, #3ea3e6 55%, #1279c4 100%)",
  sunset: "linear-gradient(180deg, #3a2a5e 0%, #c65a6a 50%, #f79a4d 100%)",
  night: "linear-gradient(180deg, #050a1e 0%, #0a1434 55%, #10204c 100%)",
};

export function IslandTravelAnimation({ destinationName, isPremium, islandType, onComplete, duration = 4200 }) {
  const [progress, setProgress] = useState(0);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const phase = getDayPhase();
  const scene = getSceneImage(islandType, isPremium);

  useEffect(() => {
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const p = Math.min(elapsed / duration, 1);
      setProgress(p);
      if (p < 1) {
        requestAnimationFrame(animate);
      } else if (!completedRef.current) {
        completedRef.current = true;
        setTimeout(() => onCompleteRef.current?.(), 350);
      }
    };
    const id = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(id);
  }, [duration]);

  const approaching = progress > 0.55;
  // destination scene: fades in and settles from a zoomed, blurred state
  const ap = Math.max(0, (progress - 0.55) / 0.45);
  const sceneOpacity = Math.min(1, ap * 1.6);
  const sceneScale = 1.35 - 0.35 * Math.min(1, ap * 1.1);
  const sceneBlur = Math.max(0, 16 - ap * 20);
  const oceanFade = 1 - Math.min(1, ap * 1.4);

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden" style={{ background: SKY[phase] }} data-testid="island-travel">
      <style>{`
        @keyframes trv-ocean { from { transform: translateY(-50%); } to { transform: translateY(0); } }
        @keyframes trv-clouds { from { transform: translateY(-55%) translateX(-2%); } to { transform: translateY(5%) translateX(2%); } }
        @keyframes trv-bank { 0%,100% { transform: rotate(-1.3deg) scale(1.02); } 50% { transform: rotate(1.3deg) scale(1.02); } }
        @keyframes trv-streak { from { transform: translateY(-120vh); opacity: 0.9; } to { transform: translateY(120vh); opacity: 0; } }
        @keyframes trv-pulse { 0%,100% { opacity: 0.7; } 50% { opacity: 1; } }
      `}</style>

      {/* First-person flight over the real ocean (aerial texture scrolling) */}
      <div className="absolute inset-0" style={{ opacity: oceanFade, animation: "trv-bank 5s ease-in-out infinite" }}>
        <div style={{ position: "absolute", left: "-10%", width: "120%", top: 0, height: "200%", animation: "trv-ocean 7s linear infinite" }}>
          <img src="/islands/ocean_aerial.jpg" alt="" draggable={false}
               style={{ width: "100%", height: "50%", objectFit: "cover", display: "block" }} />
          <img src="/islands/ocean_aerial.jpg" alt="" draggable={false}
               style={{ width: "100%", height: "50%", objectFit: "cover", display: "block", transform: "scaleY(-1)" }} />
        </div>
        {/* time-of-day grading on the water */}
        {phase === "night" && <div className="absolute inset-0" style={{ background: "rgba(6,12,40,0.72)", mixBlendMode: "multiply" }} />}
        {phase === "sunset" && <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,110,50,0.4), rgba(120,40,90,0.3))", mixBlendMode: "soft-light" }} />}
        {phase === "dawn" && <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,160,110,0.35), transparent)", mixBlendMode: "soft-light" }} />}

        {/* clouds whipping past below us */}
        <div style={{ position: "absolute", left: "-20%", width: "140%", top: 0, height: "160%",
          backgroundImage: "url(/islands/clouds.jpg)", backgroundRepeat: "repeat", backgroundSize: "120% auto",
          mixBlendMode: "screen", opacity: phase === "night" ? 0.25 : 0.55, filter: "blur(3px)",
          animation: "trv-clouds 3.2s linear infinite" }} />

        {/* speed streaks */}
        {Array.from({ length: 9 }).map((_, i) => (
          <span key={i} style={{
            position: "absolute", left: `${8 + (i * 37) % 88}%`, top: 0,
            width: 1.5, height: 90 + (i % 4) * 50,
            background: "linear-gradient(180deg, transparent, rgba(255,255,255,0.55), transparent)",
            animation: `trv-streak ${0.8 + (i % 3) * 0.25}s linear ${(i % 5) * 0.22}s infinite`,
          }} />
        ))}
        {/* vignette for depth */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(120% 100% at 50% 45%, transparent 52%, rgba(0,10,25,0.55) 100%)" }} />
      </div>

      {/* Destination island scene revealing */}
      <div className="absolute inset-0" style={{
        opacity: sceneOpacity,
        transform: `scale(${sceneScale})`,
        filter: `blur(${sceneBlur}px)`,
        willChange: "transform, opacity, filter",
      }}>
        <img src={scene} alt="" draggable={false} className="w-full h-full object-cover" />
        {phase === "night" && <div className="absolute inset-0" style={{ background: "rgba(8,14,42,0.6)", mixBlendMode: "multiply" }} />}
        {phase === "sunset" && <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,105,45,0.4), rgba(80,30,110,0.22))", mixBlendMode: "soft-light" }} />}
      </div>

      {/* Status chip */}
      <div className="absolute top-16 left-0 right-0 flex justify-center z-10">
        <div className="px-6 py-3 rounded-2xl text-center"
             style={{ background: "rgba(6,10,22,0.5)", backdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.14)" }}>
          <p className="text-white/70 text-xs font-semibold tracking-[0.25em] uppercase" style={{ animation: "trv-pulse 1.6s ease-in-out infinite" }}>
            {approaching ? "Arrivo a" : "In volo verso"}
          </p>
          <p className="text-white font-extrabold text-xl mt-0.5" data-testid="travel-destination">{destinationName}</p>
        </div>
      </div>

      {/* Progress */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 w-48 z-10">
        <div className="h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.18)" }}>
          <div className="h-full rounded-full" style={{
            width: `${progress * 100}%`,
            background: "linear-gradient(90deg, #f97316, #ec4899)",
            boxShadow: "0 0 12px rgba(236,72,153,0.7)",
          }} />
        </div>
      </div>
    </div>
  );
}

export default IslandTravelAnimation;
